import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import IndustriesGrid from "../components/IndustriesGrid";
import TrustStrip from "../components/hidroverse/TrustStrip";
import AnchorCaseStudy from "../components/hidroverse/AnchorCaseStudy";
import ByTheNumbers from "../components/hidroverse/ByTheNumbers";
import HowWeWork from "../components/hidroverse/HowWeWork";
import HomeServices from "../components/hidroverse/HomeServices";
import HomeVideoStrip from "../components/hidroverse/HomeVideoStrip";
import SupplierMarquee from "../components/hidroverse/SupplierMarquee";
import WhatSetsUsApart from "../components/hidroverse/WhatSetsUsApart";
import FAQ from "../components/hidroverse/FAQ";
import { CLIENTS } from "../lib/content";
import SEO, { orgLd, localBusinessLd } from "../components/SEO";
import Picture from "../components/Picture";

const RIBBON = CLIENTS
  .filter((c) => !/Active Mining|Operation|Boutique|Conference|Citrus|Macadamia|Sugar|Avocado|Private Utility|Local Municipality/i.test(c.name))
  .map((c) => c.name);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "26%"]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.7, 1], [1, 0.55, 0]);

  return (
    <div data-testid="home-page" style={{ background: "#FAFAF7" }}>
      <SEO
        title="Home"
        description="Cross-border water solution provider for sites that cannot afford to fail. SANS 241 documented. Contracted critical-response clause. Telemetry built in. Serving 1,500+ sites across Africa."
        path="/"
        ldJson={{
          "@context": "https://schema.org",
          "@graph": [orgLd(), localBusinessLd()],
        }}
        preloadImages={[
          { href: "/projects/thornybush/thornybush_sunset.avif", type: "image/avif" },
          { href: "/projects/thornybush/thornybush_sunset.webp", type: "image/webp" },
        ]}
      />

      {/* ============================================================
          01 · HERO — opinionated, calm, one CTA, brand mark
          ============================================================ */}
      <section
        ref={heroRef}
        className="relative overflow-hidden"
        style={{ background: "#070A0D" }}
        data-testid="hero"
      >
        {/* ===========================================================
            MOBILE HERO  (< lg)  — CINEMATIC FULL-BLEED
            Photo fills the viewport. Strong bottom gradient.
            Logo top, copy bottom — same logic as desktop just rotated.
            =========================================================== */}
        <div
          className="lg:hidden relative w-full overflow-hidden"
          data-testid="hero-mobile"
          style={{ minHeight: "100svh" }}
        >
          {/* Full-bleed Thornybush sunset */}
          <motion.div
            className="absolute inset-0"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.2, 0.7, 0.2, 1] }}
            aria-hidden
          >
            <Picture
              src="/projects/thornybush/thornybush_sunset.webp"
              alt=""
              className="w-full h-full object-cover"
              style={{ filter: "saturate(1.05) contrast(1.05)", objectPosition: "62% 50%" }}
              priority
            />
          </motion.div>

          {/* TOP gradient — extended to give the headline a clean dark backdrop in the ceiling zone */}
          <div
            className="absolute inset-x-0 top-0 pointer-events-none"
            aria-hidden
            style={{
              height: "52%",
              background:
                "linear-gradient(180deg, rgba(7,10,13,0.85) 0%, rgba(7,10,13,0.78) 30%, rgba(7,10,13,0.55) 55%, rgba(7,10,13,0.25) 80%, rgba(7,10,13,0) 100%)",
            }}
          />
          {/* BOTTOM gradient — reduced so the bright floor stays visible in the middle-right (paragraph zone) */}
          <div
            className="absolute inset-x-0 bottom-0 pointer-events-none"
            aria-hidden
            style={{
              height: "30%",
              background:
                "linear-gradient(0deg, rgba(7,10,13,0.97) 0%, rgba(7,10,13,0.88) 30%, rgba(7,10,13,0.45) 70%, rgba(7,10,13,0) 100%)",
            }}
          />

          {/* CONTENT LAYER */}
          <div className="relative z-10 flex flex-col" style={{ minHeight: "100svh" }}>
            <div className="pt-24" aria-hidden />

            {/* CEILING ZONE — headline lives here, in the photo's dark ceiling negative space.
                Rendered as a styled div with ARIA heading role so the DOM only contains a single
                <h1> across breakpoints (the desktop one). Bing/Google were flagging two H1s. */}
            <div className="px-6 mt-12">
              <motion.div
                role="heading"
                aria-level={1}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.5, ease: [0.2, 0.7, 0.2, 1] }}
                className="font-display"
                style={{
                  fontSize: "clamp(2.2rem, 9vw, 2.9rem)",
                  lineHeight: 1.04,
                  letterSpacing: "-0.026em",
                  fontWeight: 300,
                  color: "#F5F7FA",
                  textShadow: "0 4px 28px rgba(0,0,0,0.75)",
                }}
                data-testid="hero-headline-mobile"
              >
                Taking{" "}
                <span style={{ color: "#1FB6C1", fontWeight: 600 }}>pure</span>{" "}
                <span style={{ color: "#1FB6C1", fontWeight: 600 }}>water</span>{" "}
                everywhere.
              </motion.div>
            </div>

            {/* SPACER — equipment + golden doorway breathe through the middle of the viewport */}
            <div className="flex-1" />

            {/* FLOOR ZONE — body paragraph anchored bottom-right, sized small to match the
                industries strip, positioned over the bright floor so it never covers the
                equipment. Scrim is kept because the floor brightness still varies per device. */}
            <div className="pl-6 pr-2 pb-[50px] flex justify-end">
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.85 }}
                className="font-light text-right"
                style={{
                  fontSize: "0.62rem",
                  lineHeight: 1.5,
                  color: "#F5F7FA",
                  fontWeight: 500,
                  maxWidth: "62%",
                  padding: "4px 10px",
                  borderRadius: 6,
                  background: "rgba(7,10,13,0.55)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  textShadow: "0 1px 8px rgba(0,0,0,0.85)",
                }}
                data-testid="hero-body-mobile"
              >
                The <span style={{ fontWeight: 700, color: "#1FB6C1" }}>cross-border water solution provider</span> for sites that cannot afford to fail —
                end-to-end, to SANS 241 and WHO standards.
              </motion.p>
            </div>

            {/* BOTTOM — CTAs + industries over the dark gradient */}
            <div className="px-6 pb-10">
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.15 }}
                className="flex flex-col gap-3"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold"
                  style={{
                    background: "#1FB6C1",
                    color: "#070A0D",
                    fontSize: "0.78rem",
                    letterSpacing: "0.08em",
                    boxShadow: "0 14px 38px -8px rgba(31,182,193,0.6)",
                  }}
                  data-testid="hero-cta-primary-mobile"
                >
                  Contact us <span aria-hidden>→</span>
                </Link>
              </motion.div>

              {/* Pulsing scroll-down hint */}
              <motion.button
                type="button"
                onClick={() => {
                  const next = document.querySelector("[data-testid=hero]")?.nextElementSibling;
                  if (next) next.scrollIntoView({ behavior: "smooth", block: "start" });
                  else window.scrollBy({ top: window.innerHeight * 0.9, behavior: "smooth" });
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, y: [0, 6, 0] }}
                transition={{
                  opacity: { duration: 0.9, delay: 1.5 },
                  y: { duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 1.5 },
                }}
                aria-label="Scroll for more"
                className="mt-6 mx-auto flex items-center justify-center"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  border: "1px solid rgba(245,247,250,0.32)",
                  background: "rgba(7,10,13,0.5)",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                  color: "rgba(245,247,250,0.95)",
                }}
                data-testid="hero-scroll-hint-mobile"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </motion.button>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.9, delay: 1.4 }}
                className="mt-9 pt-5 border-t font-mono uppercase text-center"
                style={{
                  borderColor: "rgba(245,247,250,0.12)",
                  fontSize: "0.54rem",
                  letterSpacing: "0.3em",
                  color: "rgba(245,247,250,0.62)",
                  lineHeight: 1.9,
                }}
              >
                Lodges · Estates · Hotels<br />Mines · Agriculture · Municipal
              </motion.div>
            </div>
          </div>
        </div>

        {/* ===========================================================
            DESKTOP HERO  (lg+)  — split rail (unchanged, signed off)
            =========================================================== */}
        <div className="hidden lg:block">
        {/* CINEMATIC FULL-BLEED — Thornybush sunset, pushed right so the equipment + golden doorway live in the right 2/3 */}
        <motion.div
          className="absolute inset-0"
          style={{ y: heroY, scale: heroScale }}
          aria-hidden
        >
          <Picture
            src="/projects/thornybush/thornybush_sunset.webp"
            alt=""
            className="w-full h-full object-cover"
            style={{
              filter: "saturate(1.05) contrast(1.05)",
              objectPosition: "70% 50%",
            }}
            priority
          />
        </motion.div>

        {/* Dark rail on the LEFT — bumped 10% darker from the previous pass */}
        <div
          className="absolute inset-0"
          aria-hidden
          style={{
            background:
              "linear-gradient(90deg, rgba(7,10,13,0.85) 0%, rgba(7,10,13,0.82) 22%, rgba(7,10,13,0.57) 36%, rgba(7,10,13,0.16) 48%, rgba(7,10,13,0) 60%)",
          }}
        />
        {/* Top + bottom subtle vignettes */}
        <div
          className="absolute inset-x-0 top-0 h-28 pointer-events-none"
          aria-hidden
          style={{ background: "linear-gradient(180deg, rgba(7,10,13,0.5) 0%, rgba(7,10,13,0) 100%)" }}
        />
        <div
          className="absolute inset-x-0 bottom-0 h-32 pointer-events-none"
          aria-hidden
          style={{ background: "linear-gradient(0deg, #070A0D 0%, rgba(7,10,13,0) 100%)" }}
        />

        <motion.div
          className="relative z-10 w-full flex flex-col"
          style={{ opacity: heroOpacity, minHeight: "100svh" }}
        >
          <div className="h-24 md:h-28" />

          {/* TEXT RAIL — locked to the dark left ~36% of viewport. 3 flex children: headline TOP, paragraph CENTER, CTAs BOTTOM */}
          <div className="flex-1 grid lg:grid-cols-12 px-6 md:px-12 lg:px-20">
            <div className="lg:col-span-5 xl:col-span-4 flex flex-col justify-between pt-6 md:pt-10 pb-4">
              {/* TOP: headline */}
              <motion.h1
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: [0.2, 0.7, 0.2, 1] }}
                className="font-display max-w-[520px]"
                style={{
                  fontSize: "clamp(2rem, 3.9vw, 4rem)",
                  lineHeight: 1.03,
                  letterSpacing: "-0.025em",
                  fontWeight: 300,
                  color: "#F5F7FA",
                  textShadow: "0 4px 28px rgba(0,0,0,0.7)",
                }}
                data-testid="hero-headline"
              >
                Taking <span style={{ color: "#1FB6C1", fontWeight: 600 }}>pure</span>{" "}
                <br />
                <span className="whitespace-nowrap"><span style={{ color: "#1FB6C1", fontWeight: 600 }}>water</span> everywhere.</span>
              </motion.h1>

              {/* CENTER: paragraph, vertically centered between headline and buttons */}
              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.8 }}
                className="font-light max-w-[480px]"
                style={{
                  fontSize: "clamp(0.92rem, 1vw, 1.02rem)",
                  lineHeight: 1.6,
                  color: "rgba(245,247,250,0.82)",
                  textShadow: "0 2px 14px rgba(0,0,0,0.7)",
                }}
              >
                The <span style={{ fontWeight: 700 }}>cross-border water solution provider</span> for sites that cannot afford to fail.
                We bring the right team to your project and own the outcome end-to-end —
                under a dedicated project, to SANS 241 and WHO standards.
              </motion.p>

              {/* BOTTOM: CTAs, sitting just above the industries strip */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.2 }}
                className="flex flex-wrap gap-3"
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 md:px-7 py-3 md:py-3.5 rounded-full font-semibold transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: "#1FB6C1",
                    color: "#070A0D",
                    fontSize: "0.74rem",
                    letterSpacing: "0.08em",
                    boxShadow: "0 12px 40px -8px rgba(31,182,193,0.55)",
                  }}
                  data-testid="hero-cta-primary"
                >
                  Contact us <span aria-hidden>→</span>
                </Link>
                <Link
                  to="/projects"
                  className="inline-flex items-center gap-2 px-6 md:px-7 py-3 md:py-3.5 rounded-full font-mono uppercase transition-all duration-300 hover:opacity-80"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    color: "rgba(245,247,250,0.92)",
                    fontSize: "0.62rem",
                    letterSpacing: "0.18em",
                    border: "1px solid rgba(245,247,250,0.22)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  }}
                  data-testid="hero-cta-secondary"
                >
                  See the work →
                </Link>
              </motion.div>
            </div>

            <div className="hidden lg:block lg:col-span-7 xl:col-span-8" aria-hidden />
          </div>

          {/* BRAND WORDMARK — bottom-right, teal + pure-black variant, raw png on the bright floor */}
          <motion.img
            src="/brand/hidroverse_wordmark_black.webp"
            alt="HidroVerse"
            className="select-none absolute hidden lg:block"
            draggable={false}
            initial={{ opacity: 0, scale: 0.94, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
            transition={{ duration: 1.3, ease: [0.2, 0.7, 0.2, 1], delay: 1.4 }}
            style={{
              right: "5%",
              bottom: "22%",
              maxWidth: "320px",
              height: "auto",
              filter: "drop-shadow(0 2px 6px rgba(255,255,255,0.45))",
              zIndex: 20,
            }}
            data-testid="hero-brand-mark"
          />

          {/* Bottom edge — industries strip (left) + project credit pill (right) */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 1.8 }}
            className="relative px-6 md:px-12 lg:px-20 pb-8 md:pb-12"
          >
            <div className="max-w-[1500px] mx-auto flex items-end justify-between gap-6 flex-wrap">
              <div
                className="font-mono uppercase"
                style={{ fontSize: "0.58rem", letterSpacing: "0.3em", color: "rgba(245,247,250,0.62)" }}
              >
                Lodges · Estates · Hotels · Mines · Agriculture · Municipal
              </div>
              <div
                className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full font-mono uppercase"
                style={{
                  background: "rgba(7,10,13,0.7)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  border: "1px solid rgba(245,247,250,0.14)",
                  fontSize: "0.56rem",
                  letterSpacing: "0.24em",
                  color: "rgba(245,247,250,0.92)",
                }}
                data-testid="hero-credit"
              >
                <span style={{ color: "#1FB6C1", fontSize: "0.7rem" }}>●</span>
                Thornybush Game Lodge · HidroVerse install
              </div>
            </div>
          </motion.div>
        </motion.div>
        </div>
      </section>

      {/* ============================================================
          02 · SERVICES — plain-language entry point to /services
          ============================================================ */}
      <HomeServices />

      {/* ============================================================
          02.5 · WHAT SETS US APART — 9 structural differentiators
          ============================================================ */}
      <WhatSetsUsApart />

      {/* ============================================================
          03 · ANCHOR CASE STUDY — Kruger Park Lodge
          ============================================================ */}
      <AnchorCaseStudy />

      {/* ============================================================
          04 · BY THE NUMBERS — real metrics in mono
          ============================================================ */}
      <ByTheNumbers />

      {/* ============================================================
          05 · TRUST STRIP — compact, after the numbers
          ============================================================ */}
      <TrustStrip />

      {/* ============================================================
          05.25 · SUPPLIER MARQUEE — tier-1 equipment partners (combined)
          ============================================================ */}
      <SupplierMarquee tone="light" />

      {/* ============================================================
          05.5 · VIDEO STRIP — 3 most-recent shorts, lightbox playback
          ============================================================ */}
      <HomeVideoStrip />

      {/* ============================================================
          06 · HOW WE WORK — section removed per user feedback (covered on /about)
          ============================================================ */}

      {/* ============================================================
          06 · INDUSTRIES — light section
          ============================================================ */}
      <IndustriesGrid tone="light" />

      {/* ============================================================
          07 · CLIENT RIBBON — Portfolio
          ============================================================ */}
      <section
        className="relative overflow-hidden border-y"
        style={{ background: "#0E1318", borderColor: "rgba(255,255,255,0.06)", color: "#F5F7FA" }}
        data-testid="client-ribbon"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pt-10 md:pt-14 pb-6 md:pb-8">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div
                className="font-mono uppercase mb-4"
                style={{ fontSize: "0.62rem", letterSpacing: "0.26em", color: "#1FB6C1" }}
              >
                Our Clients
              </div>
              <h3
                className="font-display"
                style={{
                  fontSize: "clamp(1.6rem, 2.8vw, 2.4rem)",
                  lineHeight: 1.15,
                  fontWeight: 500,
                  letterSpacing: "-0.015em",
                  color: "#F5F7FA",
                }}
              >
                Lodges, hotels, mines, estates<br />
                <span style={{ color: "#1FB6C1" }}>and municipalities we serve.</span>
              </h3>
            </div>
            <Link
              to="/clients"
              className="inline-flex items-center gap-2 font-mono uppercase"
              style={{ fontSize: "0.66rem", letterSpacing: "0.24em", color: "#F5F7FA" }}
              data-testid="ribbon-see-all"
            >
              Full portfolio <span aria-hidden>→</span>
            </Link>
          </div>
        </div>
        <div className="pb-10 md:pb-14">
          <div className="hv-client-ribbon" data-testid="ribbon-track">
            {[...RIBBON, ...RIBBON].map((name, i) => (
              <span key={`${name}-${i}`} className="hv-client-name" style={{ color: "rgba(245,247,250,0.78)" }}>
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================
          08 · FAQ — operator-facing questions
          ============================================================ */}
      <FAQ />

      {/* ============================================================
          09 · CONTACT US — closing CTA (tightened padding)
          ============================================================ */}
      <section
        className="px-6 md:px-12 lg:px-20 py-14 md:py-20"
        style={{ background: "#0E1318", color: "#F5F7FA" }}
        data-testid="home-cta"
      >
        <div className="max-w-5xl mx-auto text-center">
          <div
            className="font-mono uppercase mb-5"
            style={{ fontSize: "0.62rem", letterSpacing: "0.3em", color: "#1FB6C1" }}
          >
            Contact us
          </div>
          <h2
            className="font-display mb-6"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.4rem)",
              lineHeight: 1.06,
              letterSpacing: "-0.02em",
              fontWeight: 700,
              color: "#F5F7FA",
            }}
          >
            Tell us how we can<br />
            <span style={{ color: "#1FB6C1" }}>assist with your water.</span>
          </h2>
          <p
            className="max-w-2xl mx-auto font-light leading-relaxed mb-8"
            style={{ fontSize: "1rem", color: "#9AA7B4" }}
          >
            For personalised assistance and solutions, contact our specialists.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-[1.02]"
            style={{ background: "#1FB6C1", color: "#070A0D", fontSize: "0.82rem", letterSpacing: "0.06em" }}
            data-testid="cta-request-quote"
          >
            Contact us <span aria-hidden>→</span>
          </Link>
        </div>
      </section>
    </div>
  );
}
